import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, ArrowLeft, Check } from "lucide-react";

/**
 * Public self-service signup: cria empresa + usuário owner com trial de 14 dias.
 * Requer aceite dos Termos de Uso.
 */
const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    owner_name: "",
    email: "",
    password: "",
    phone: "",
    accept_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) {
      toast.error("Você precisa aceitar os Termos de Uso para continuar.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("A senha precisa ter no mínimo 6 caracteres.");
      return;
    }
    setSubmitting(true);
    const res = await signup(form);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Bem-vindo, ${res.user.name}! Trial de 14 dias iniciado.`);
      navigate("/admin", { replace: true });
    } else {
      toast.error(res.error || "Falha ao criar conta");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 py-12">
      <Card className="w-full max-w-lg p-8 md:p-10 border border-border/80 rounded-none shadow-none">
        <Link
          to="/"
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-foreground mb-8"
          data-testid="signup-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <UserPlus className="w-6 h-6" />
          <span className="text-xs uppercase tracking-[0.25em] text-foreground/60">Cadastro Gratuito</span>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter mb-2">Criar conta</h1>
        <p className="text-sm text-foreground/60 mb-8">
          14 dias grátis, sem cartão. Cadastre sua empresa em menos de 1 minuto.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="company_name" className="text-xs uppercase tracking-[0.2em]">Nome da empresa</Label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={set("company_name")}
              placeholder="Ex.: Clínica Saúde+"
              className="mt-2 h-12 text-base rounded-none"
              data-testid="signup-company-input"
              required
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="owner_name" className="text-xs uppercase tracking-[0.2em]">Seu nome</Label>
            <Input
              id="owner_name"
              value={form.owner_name}
              onChange={set("owner_name")}
              placeholder="Maria Silva"
              className="mt-2 h-12 text-base rounded-none"
              data-testid="signup-name-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em]">Email</Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="voce@empresa.com"
              className="mt-2 h-12 text-base rounded-none"
              data-testid="signup-email-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em]">Telefone / WhatsApp</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="(11) 99999-9999"
              className="mt-2 h-12 text-base rounded-none"
              data-testid="signup-phone-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em]">Senha</Label>
            <Input
              id="password"
              type="password"
              value={form.password}
              onChange={set("password")}
              placeholder="Mínimo 6 caracteres"
              className="mt-2 h-12 text-base rounded-none"
              data-testid="signup-password-input"
              required
              minLength={6}
            />
          </div>

          <label className="flex items-start gap-3 pt-2 cursor-pointer group" data-testid="signup-terms-label">
            <input
              type="checkbox"
              checked={form.accept_terms}
              onChange={set("accept_terms")}
              className="mt-1 w-5 h-5 accent-foreground cursor-pointer shrink-0"
              data-testid="signup-terms-checkbox"
              required
            />
            <span className="text-sm text-foreground/80 leading-relaxed">
              Li e aceito os{" "}
              <Link
                to="/termos-de-uso"
                target="_blank"
                className="underline underline-offset-2 font-semibold hover:text-foreground"
                data-testid="signup-terms-link"
              >
                Termos de Uso
              </Link>{" "}
              e a política de tratamento de dados (LGPD).
            </span>
          </label>

          <Button
            type="submit"
            disabled={submitting || !form.accept_terms}
            className="w-full h-12 rounded-none text-base uppercase tracking-[0.2em] font-semibold mt-6"
            data-testid="signup-submit-btn"
          >
            {submitting ? "Criando conta…" : "Iniciar Trial de 14 dias"}
          </Button>

          <div className="pt-4 border-t border-border/40 grid grid-cols-3 gap-3">
            {["Sem cartão", "Cancele quando quiser", "Suporte por e-mail"].map((benefit) => (
              <div key={benefit} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-foreground/60">
                <Check className="w-3 h-3 shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-foreground/50 leading-relaxed pt-4">
            Já tem conta?{" "}
            <Link to="/login" className="underline font-semibold hover:text-foreground" data-testid="signup-goto-login">
              Entrar
            </Link>
          </p>
        </form>
      </Card>
    </div>
  );
};

export default Signup;
